/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : testCasesActions.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import actionType from "../actionTypes";

const testCasesActions = {
  // Upload & get files
  upload_files: {
    api_name: "/UCM/files/upload",
    https_method: "POST",
    action_type: "",
    description: "Upload files",
  },
  get_files: {
    api_name: "/UCM/files/details",
    https_method: "GET",
    action_type: "",
    description: "Get uploaded files",
  },
  // Test cases - Repository page
  get_test_cases: {
    api_name: "/TCM/testcase/view",
    https_method: "GET",
    action_type: actionType.GET_TEST_CASES,
    description: "Get test cases",
  },
  add_test_case: {
    api_name: "/TCM/testcase/add",
    https_method: "POST",
    action_type: actionType.ADD_TEST_CASE,
    description: "Add test case",
  },
  update_test_case: {
    api_name: "/TCM/testcase/update",
    https_method: "PUT",
    action_type: actionType.UPDATE_TEST_CASE,
    description: "Update test case",
  },
  // Test cases - Campaign page
  get_test_campaigns: {
    api_name: "/TCM/testcase/campaign/view",
    https_method: "GET",
    action_type: actionType.GET_TEST_CAMPAIGNS,
    description: "Get test campaigns",
  },
  create_campaign_run: {
    api_name: "/TCM/testcase/run/add",
    https_method: "POST",
    action_type: "",
    description: "Create test campaign run",
  },
  add_test_campaign: {
    api_name: "/TCM/testcase/campaign/add",
    https_method: "POST",
    action_type: actionType.ADD_TEST_CAMPAIGN,
    description: "Add test campaign",
  },
  update_test_campaign: {
    api_name: "/TCM/testcase/campaign/update",
    https_method: "PUT",
    action_type: "",
    description: "Update test campaign",
  },
  get_test_campaign: {
    api_name: "/TCM/testcase/campaign/view",
    https_method: "GET",
    action_type: "",
    description: "Get single test campaign details by campaign_id",
  },
  link_test_cases: {
    api_name: "/TCM/testcase/campaign/link/add",
    https_method: "POST",
    action_type: "",
    description: "Link test campaign to test cases",
  },
  get_campaign_test_cases: {
    api_name: "/TCM/testcase/campaign/link/view",
    https_method: "GET",
    action_type: actionType.GET_CAMPAIGN_TEST_CASES,
    description: "Get test cases",
  },
  get_campaign_run_test_cases: {
    api_name: "/TCM/testcase/run/view",
    https_method: "GET",
    action_type: "",
    description: "Get test campaign run test cases",
  },
  get_campaign_result_test_cases: {
    api_name: "/TCM/testcase/run/result/view",
    https_method: "GET",
    action_type: "",
    description: "Get test campaign run result test cases",
  },
  get_latest_campaign_result_test_cases: {
    api_name: "/TCM/testcase/run/result/latest",
    https_method: "GET",
    action_type: "",
    description: "Get latest test campaign run result test cases",
  },
  add_campaign_result_test_cases: {
    api_name: "/TCM/testcase/run/result/add",
    https_method: "POST",
    action_type: "",
    description: "Get test campaign run result test cases",
  },
  // Project
  get_projects: {
    api_name: "/PCM/project/details",
    https_method: "GET",
    action_type: "",
    description: "Get projects",
  },
  // Variant
  get_variants: {
    api_name: "/PCM/project/variants",
    https_method: "GET",
    action_type: "",
    description: "Get variants",
  },
  // EE Component
  get_ee_components: {
    api_name: "/VLC/ee_components/details",
    https_method: "GET",
    action_type: "",
    description: "Get ee components",
  },
  // Software Module
  get_software_modules: {
    api_name: "/VLC/swm/details",
    https_method: "GET",
    action_type: "",
    description: "Get software modules",
  },
  // Vehicle System
  get_vehicle_systems: {
    api_name: "/VLC/vehicle_systems/details",
    https_method: "GET",
    action_type: "",
    description: "Get Vehicle Systems",
  },
  // Vehicle Component
  get_vehicle_components: {
    api_name: "/VLC/vehicle_components/details",
    https_method: "GET",
    action_type: "",
    description: "Get Vehicle Components",
  },
  // Legal standard
  get_legal_standards: {
    api_name: "/LCM/legal/standard/view",
    https_method: "GET",
    action_type: "",
    description: "",
  },
  // Legal requirement
  get_legal_requirements: {
    api_name: "/LCM/legal/requirement/view",
    https_method: "GET",
    action_type: "",
    description: "",
  },
  // Get components details
  get_components_details: {
    api_name: "/TCM/testcase/entity/view",
    https_method: "POST",
    action_type: "",
    description: "Get ee/swm/vs/vc/lr details by ids",
  },
  // Test case artifacts
  get_test_case_artifacts: {
    api_name: "/TCM/testcase/artefact/view",
    https_method: "POST",
    action_type: "",
    description: "Get test case artifacts",
  },
  create_test_case_artifacts: {
    api_name: "/TCM/testcase/artefact/save-to-ticket",
    https_method: "POST",
    action_type: "",
    description: "Create test case artifacts",
  },
  // EE Component details
  get_ee_components_globally: {
    api_name: "/VLC/ee_components/search",
    https_method: "GET",
    action_type: "",
    description: "Get ee components",
  },
  // SWM Component details
  get_swm_globally: {
    api_name: "/VLC/swm/search",
    https_method: "GET",
    action_type: "",
    description: "Get swm components",
  },
  // VS Component details
  get_vs_globally: {
    api_name: "/VLC/vehicle_systems/search",
    https_method: "GET",
    action_type: "",
    description: "Get vehicle systems",
  },
  // VC Component details
  get_vc_globally: {
    api_name: "/VLC/vehicle_components/search",
    https_method: "GET",
    action_type: "",
    description: "Get vehicle components",
  },

  // Recreate actions
  get_Parent_test_campaign: {
    api_name: "/RCS/campaign/get",
    https_method: "GET",
    action_type: "",
    description: "",
  },
  recreate_test_campaign: {
    api_name: "/RCS/campaign/recreate",
    https_method: "POST",
    action_type: "",
    description: "Recreate Test Campaign",
  },
  get_Parent_test_case_for_campaign: {
    api_name: "/RCS/campaign/testcase/get",
    https_method: "GET",
    action_type: "",
    description: "",
  },
};

export default testCasesActions;
